import React from "react";
import { FormHelperText, Box, Select, MenuItem } from "@material-ui/core";
import { makeStyles, MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import { useField } from "formik";
import PropTypes from "prop-types";

const useStyles = makeStyles({
  outlinedBorder: {
    fontFamily: "'Montserrat', sans-serif",
    background: "#fff",
    borderRadius: "0.8vh",
    width: "100%"
  },
  outlinedBorderRed: {
    background: "#fff",
    borderRadius: "0.8vh",
    width: "100%"
  }
});

const theme = createTheme({
  overrides: {
    MuiSelect: {
      select: {
        backgroundColor: "transparent",
        "&:focus": {
          backgroundColor: "transparent",
        },
      },
      selectMenu: {
        backgroundColor: "transparent",
      },
      icon: {
        fontSize: "calc(16 / 910 * 100vh)",
        right: "calc(12 / 910 * 100vh)",
        top: "50%",
        transform: "translateY(-50%)",
      },
      iconOutlined: {
        fontSize: "calc(16 / 910 * 100vh)",
        right: "calc(12 / 910 * 100vh)",
        top: "50%",
        transform: "translateY(-50%)",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: "0.8vh",
        backgroundColor: "transparent",
        "& $notchedOutline": {
          borderColor: "#C0C0C0",
          borderWidth: "0.15vh"
        },
        "&:hover $notchedOutline": {
          borderColor: "#C0C0C0"
        },
        "&$focused $notchedOutline": {
          borderColor: "#C0C0C0",
          borderWidth: "0.15vh",
        },
        "&$error $notchedOutline": {
          borderColor: "#B53E4E"
        }
      },
      input: {
        backgroundColor: "transparent",
      },
      notchedOutline: {
        borderColor: "#C0C0C0",
        borderWidth: "0.15vh"
      }
    }
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif"
  },
});

// Keep Select height consistent with the form fields (44px at 900px viewport height).
const FIELD_H = "calc(44 / 910 * 100vh)";
const FONT_16 = "calc(16 / 910 * 100vh)";
const SELECT_PAD_X = "calc(14 / 910 * 100vh)";

const styles = {
  height: 24,
  labelOffset: 110,
  focused: true,
  textFiledStyle: {
    color: "#4a4a4a",
    width: "100%",
    height: 24,
    lineHeight: 1.5,
    borderRadius: "0.8vh",
    fontSize: FONT_16,
    border: "0.15vh solid #195091"
  },
  helpTextStyle: {
    color: "#030F09",
    fontSize: "clamp(1.45vh, 0.85vw, 1.75vh)",
    paddingLeft: "1vw"
  },
  helpTextErrorStyle: {
    color: "#B53E4E",
    fontSize: "clamp(1.45vh, 0.85vw, 1.75vh)",
    paddingLeft: "1vw"
  }
};

function ComboSelectBox(props) {
  const [filed, meta] = useField(props);
  const classes = useStyles();
  return (
    <MuiThemeProvider theme={theme}>
      <Box style={{ marginTop: 0, marginBottom: 0 }}>
        <Select
          {...filed}
          {...props}
          className={
            meta.touched && Boolean(meta.error)
              ? classes.outlinedBorderRed
              : classes.outlinedBorder
          }
          style={{
            height: FIELD_H,
            fontSize: FONT_16,
            ...props.style,
          }}
          variant="outlined"
          displayEmpty
          MenuProps={{
            PaperProps: {
              style: {
                fontSize: FONT_16,
              },
            },
          }}
          renderValue={(value) => {
            if (value === "") {
              return (
                <span
                  style={{
                    color: "#aaa",
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  {props.displayLabel}
                </span>
              );
            }
            const selectedOption = props.options.find(opt => opt.value === value);
            return (
              <span style={{ display: "flex", alignItems: "center", height: "100%" }}>
                {selectedOption ? selectedOption.title : value}
              </span>
            );
          }}
          onChange={e => {
            filed["value"] = e.target.value;
            filed.onChange(e);
            meta.value = e.target.value;
            props.onChange && props.onChange(e);
          }}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched ? meta.error : ""}
          type="select"
          InputLabelProps={{
            style: {
              height: styles.height,
              ...(!styles.focused && { top: `${styles.labelOffset}px` })
            }
          }}
  inputProps={{
    style: {
      height: FIELD_H,
      padding: `0 ${SELECT_PAD_X}`,
      display: "flex",
      alignItems: "center",
      fontSize: FONT_16
    }
  }}
        >
          <MenuItem value="" disabled>
            <span style={{ color: "#aaa" }}>{props.displayLabel}</span>
          </MenuItem>
          {props.options.map(item => (
            <MenuItem
              value={item.value}
              key={"menu_item" + item.value}
              style={{ fontSize: FONT_16 }}
            >
              {item.title}
            </MenuItem>
          ))}
        </Select>
        {meta.touched && Boolean(meta.error) && (
          <FormHelperText
            color="primary"
            style={{
              ...styles.helpTextErrorStyle,
              marginTop: meta.touched && meta.error ? "1.2vh" : "-0.6vh"
            }}
          >
            {meta.error}
          </FormHelperText>
        )}
      </Box>
    </MuiThemeProvider>
  );
}
ComboSelectBox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, title: PropTypes.string })
  ),
  displayLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func
};
export default ComboSelectBox;
